import type { ClientInvite } from '~/stores/clients'
import type { FirmSummaryClient } from '~/stores/dashboard'

/**
 * Presentation for the Clients roster's composed cells — the status bucket, the
 * EOR funding state, and the Issues count. Kept here, pure and
 * exported, for the same reason `staffStatusChip` is: `RiseTable` is
 * `@ts-nocheck` and renders each cell from a template slot, so the mapping logic
 * is untestable inside the `.vue` and trivially testable out here.
 */

type StatusBucket = FirmSummaryClient['status_bucket']
type FundingStatus = FirmSummaryClient['funding_status']

export interface RosterChip {
  label: string
  /** Tailwind background + text classes for the pill. */
  class: string
  /** Optional Remix icon class rendered inside the pill. */
  icon?: string
  /**
   * Whether the icon should spin. Only the activating chip sets it: its loader
   * icon is otherwise static, which reads as a stuck page rather than as work in
   * flight. Kept on the chip so the motion is a property of the state, not a
   * template special-case.
   */
  spin?: boolean
}

/**
 * The firm's read on a client, one of three buckets the summary computes. A
 * value outside the set falls back to a neutral grey pill rather than an
 * unstyled one, so an enum the contract grows before the portal is redeployed
 * still reads as a chip — the same fallback discipline `statusChipClass` keeps.
 */
const NEUTRAL_CHIP = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'

const STATUS_CHIPS: Record<string, RosterChip> = {
  good_standing: {
    label: 'Good',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
    icon: 'ri-checkbox-circle-line',
  },
  need_action: {
    label: 'Action Needed',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
    icon: 'ri-error-warning-line',
  },
  onboarding: {
    label: 'Onboarding',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
    icon: 'ri-time-line',
  },
}

export function statusChip(bucket: StatusBucket): RosterChip {
  return STATUS_CHIPS[bucket] ?? { label: bucket, class: NEUTRAL_CHIP }
}

/**
 * A client whose bridge deploy is still in flight — the ~2-3 min between the
 * client accepting and the deploy confirming. It is shown on the roster so the
 * firm does not lose sight of a client it just engaged, but it is not yet
 * actionable. Its own chip, not a status bucket: the backend can only bucket it as
 * `onboarding`, which understates that the wait is on chain rather than on the
 * client.
 */
const ACTIVATING_CHIP: RosterChip = {
  label: 'Activating',
  class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100',
  icon: 'ri-loader-4-line',
  spin: true,
}

/**
 * The chip a roster row shows: `Activating` while its bridge is still deploying,
 * otherwise the firm's status bucket. Takes the whole row rather than the bucket
 * alone so the activation state — a separate field — can take precedence over the
 * bucket the summary computed.
 */
export function rosterStatusChip(
  row: Pick<FirmSummaryClient, 'status_bucket' | 'activation_status'>,
): RosterChip {
  if (row.activation_status === 'activating')
    return ACTIVATING_CHIP

  return statusChip(row.status_bucket)
}

/**
 * Whether a roster row is still activating on chain, and so not yet navigable: the
 * client-detail page only assigns accountants to a bridge that has code, and an
 * activating client's bridge is still deploying.
 */
export function isActivating(
  row: Pick<FirmSummaryClient, 'activation_status'>,
): boolean {
  return row.activation_status === 'activating'
}

/**
 * The client's EOR funding state, or `null` when the client has no EOR snapshot
 * at all. `null` is deliberately *not* "funded": it is the absence of the
 * signal, so it returns no chip and the cell renders a plain dash. Only a real
 * value — including `funded` — earns a labelled badge, because "Funded" is
 * itself information the firm wants to see confirmed, not just its absence of a
 * warning.
 */
const FUNDING_CHIPS: Record<string, RosterChip> = {
  funded: {
    label: 'Funded',
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  },
  partially_funded: {
    label: 'Partial',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
  },
  unfunded: {
    label: 'Unfunded',
    class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  overdue: {
    label: 'Overdue',
    class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
}

export function fundingChip(status: FundingStatus): RosterChip | null {
  if (status === null)
    return null

  return FUNDING_CHIPS[status] ?? { label: status, class: NEUTRAL_CHIP }
}

/**
 * The "N issues" label for the Issues cell, or `null` when a client has none —
 * a clear count is the point, so zero renders a dash rather than a `0 issues`
 * pill that reads as a to-do the firm doesn't have. An issue is one of the
 * client's Action Items rows still needing attention (`issues_count`), the same
 * rule as the left-menu counter.
 */
export function issuesLabel(count: number): string | null {
  if (count <= 0)
    return null

  return `${count} ${count === 1 ? 'issue' : 'issues'}`
}

/**
 * Where an invitation stands, so a firm can tell a client who declined from one
 * who has not opened the email yet (riseworks/projects#8598). Same neutral
 * fallback as the other chips for a status the contract grows before the portal
 * is redeployed.
 */
const INVITE_CHIPS: Record<string, RosterChip> = {
  pending: {
    label: 'Pending',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
    icon: 'ri-time-line',
  },
  rejected: {
    label: 'Declined',
    class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-close-circle-line',
  },
  expired: {
    label: 'Expired',
    class: NEUTRAL_CHIP,
    icon: 'ri-history-line',
  },
}

export function inviteStatusChip(status: ClientInvite['status']): RosterChip {
  return INVITE_CHIPS[status] ?? { label: status, class: NEUTRAL_CHIP }
}
